import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Download,
  Search,
  Loader2,
  Check,
  ChevronDown,
  Pencil,
  Eye,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useNav, matchRoute } from "@/context/NavContext";
import { BailPaymentDialog } from "@/components/BailPaymentDialog";
import { useAuth } from "@/context/AuthContext";

const INTER = "'Inter', system-ui, -apple-system, sans-serif";
const PLAYFAIR = "'Playfair Display', Georgia, serif";
const GOLD = "#B08A45";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* -------------------------------------------------------------------------- */
/*  Placeholder keys (must stay in sync with backend template)                */
/* -------------------------------------------------------------------------- */
const ALL_KEYS = [
  "COURT_NAME", "COURT_PLACE", "POLICE_STATION", "DISTRICT",
  "CASE_PREFIX", "CRIME_NO", "CRIME_YEAR",
  "COMPLAINANT", "PARTY1_LABEL",
  "PARTY2_NAME", "PARTY2_NO", "PARTY2_LABEL",
  "DEPONENT_NAME", "DEPONENT_AGE", "DEPONENT_OCC", "DEPONENT_ADDRESS", "DEPONENT_STATE",
  "RELATION",
  "CASE_STATE", "FIR_NO", "FIR_DATE", "SECTIONS", "MAX_PUNISHMENT",
  "ARREST_DATE", "CUSTODY_STATUS", "CUSTODY_PERIOD",
  "STAGE", "WITNESSES_CITED", "WITNESSES_EXAMINED",
  "NBW", "PROCLAIMED", "OTHER_COERCIVE",
  "VERIFICATION_PLACE", "VERIFICATION_DAY", "VERIFICATION_MONTH", "VERIFICATION_YEAR",
];
const DEFAULT_VALUES = ALL_KEYS.reduce((a, k) => ({ ...a, [k]: "" }), {});
// Dynamic-row fields — arrays of row-objects, initialized empty so the UI
// starts with one blank row for each section.
DEFAULT_VALUES.ANT_ROWS = [];
DEFAULT_VALUES.PREV_BAIL_ROWS = [];

// Sensible defaults
DEFAULT_VALUES.CASE_PREFIX = "C.R. No.";
DEFAULT_VALUES.PARTY1_LABEL = "Complainant";
DEFAULT_VALUES.PARTY2_LABEL = "Accused";

const COURT_OPTIONS = [
  "District & Sessions Court",
  "Additional Sessions Court",
  "Chief Judicial Magistrate",
  "Judicial Magistrate First Class",
  "Bombay High Court",
  "Bombay High Court Circuit Bench",
];

const CASE_PREFIX_SUGGESTIONS = [
  "C.R. No.",
  "Criminal Bail No.",
  "Criminal Anticipatory Bail No.",
  "R.C.C. No.",
  "Crime No.",
  "Sessions Case No.",
  "Spl. Case No.",
  "Special Case No.",
  "Misc. Application No.",
];

const PARTY1_OPTIONS = [
  "Complainant",
  "Applicant",
  "Plaintiff",
  "Petitioner",
  "Appellant",
];

const PARTY2_OPTIONS = [
  "Accused",
  "Opponent",
  "Defendant",
  "Respondent",
];

const RELATION_OPTIONS = [
  "Husband",
  "Wife",
  "Father",
  "Mother",
  "Brother",
  "Sister",
  "Son",
  "Daughter",
  "Client",
];

/* -------------------------------------------------------------------------- */
/*  Step config — 4 steps only (no Review, no Preview)                        */
/* -------------------------------------------------------------------------- */
const STEPS = [
  { id: "court", title: "Court Details", subtitle: "Where is the case filed?" },
  { id: "deponent", title: "Deponent Details", subtitle: "Who is filing this affidavit?" },
  { id: "case", title: "Case Details", subtitle: "About the FIR and offence" },
  { id: "verify", title: "Verification", subtitle: "Final signatures" },
];

/* -------------------------------------------------------------------------- */
/*  Small primitives                                                          */
/* -------------------------------------------------------------------------- */
function TextField({ label, testId, value, onChange, placeholder, type = "text", listId, options }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11.5px] font-medium tracking-wide text-stone-500">
        {label}
      </div>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        list={listId}
        className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] text-stone-900 outline-none transition-colors focus:border-stone-400"
      />
      {options && listId ? (
        <datalist id={listId}>
          {options.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      ) : null}
    </label>
  );
}

function TextArea({ label, testId, value, onChange, placeholder, rows = 2 }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11.5px] font-medium tracking-wide text-stone-500">
        {label}
      </div>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        data-testid={testId}
        className="w-full resize-y rounded-xl border border-stone-200 bg-white px-3 py-2 text-[13.5px] text-stone-900 outline-none transition-colors focus:border-stone-400"
      />
    </label>
  );
}

function SelectField({ label, testId, value, onChange, options }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11.5px] font-medium tracking-wide text-stone-500">
        {label}
      </div>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="h-10 w-full appearance-none rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] text-stone-900 outline-none transition-colors focus:border-stone-400"
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}

function CourtDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const filtered = COURT_OPTIONS.filter((o) =>
    o.toLowerCase().includes(q.trim().toLowerCase())
  );
  return (
    <label className="block" ref={ref}>
      <div className="mb-1 text-[11.5px] font-medium tracking-wide text-stone-500">
        Court Name
      </div>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="input-COURT_NAME"
          className="flex h-10 w-full items-center justify-between rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] text-stone-900 outline-none focus:border-stone-400"
        >
          <span className={value ? "" : "text-stone-400"}>
            {value || "Select court…"}
          </span>
          <ChevronDown className="h-4 w-4 text-stone-500" />
        </button>
        {open && (
          <div className="absolute left-0 right-0 top-full z-30 mt-1.5 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-lg">
            <div className="flex items-center gap-2 border-b border-stone-100 px-3 py-2">
              <Search className="h-4 w-4 text-stone-400" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search court…"
                className="w-full border-0 bg-transparent text-[13px] outline-none"
                data-testid="input-COURT_NAME-search"
              />
            </div>
            <div className="max-h-56 overflow-auto">
              {filtered.length === 0 ? (
                <div className="px-4 py-3 text-[13px] text-stone-400">
                  No matches
                </div>
              ) : (
                filtered.map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => {
                      onChange(o);
                      setOpen(false);
                      setQ("");
                    }}
                    data-testid={`court-opt-${o.replace(/[^A-Za-z0-9]+/g, "-")}`}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-[13px] hover:bg-stone-50"
                  >
                    <span>{o}</span>
                    {o === value ? (
                      <Check className="h-4 w-4" style={{ color: GOLD }} />
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </label>
  );
}

/**
 * Responsive row helper — mobile: 1 col, tablet: 2 cols, desktop honours `cols`.
 */
function Row({ cols = 2, children }) {
  const cls =
    cols === 3
      ? "grid grid-cols-1 gap-2.5 sm:grid-cols-2 md:grid-cols-3"
      : cols === 2
      ? "grid grid-cols-1 gap-2.5 sm:grid-cols-2"
      : "grid grid-cols-1 gap-2.5";
  return <div className={cls}>{children}</div>;
}

/* -------------------------------------------------------------------------- */
/*  Step progress header                                                       */
/* -------------------------------------------------------------------------- */
function StepHeader({ stepIdx }) {
  const pct = Math.round(((stepIdx + 1) / STEPS.length) * 100);
  const step = STEPS[stepIdx];
  return (
    <div className="sticky top-14 z-10 border-b border-stone-200/80 bg-[#FAF8F4]/95 pb-3 pt-3 backdrop-blur-md">
      <div className="mx-auto max-w-4xl px-4">
        <div className="flex items-baseline justify-between">
          <div>
            <div
              className="text-[10.5px] font-semibold uppercase tracking-[0.2em]"
              style={{ color: GOLD }}
              data-testid="step-counter"
            >
              Step {stepIdx + 1} of {STEPS.length}
            </div>
            <div
              className="mt-1 text-stone-900"
              style={{
                fontFamily: PLAYFAIR,
                fontSize: "clamp(1.4rem, 3vw, 1.8rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
              }}
              data-testid="step-title"
            >
              {step.title}
            </div>
            <div className="mt-0.5 text-[12.5px] text-stone-500">
              {step.subtitle}
            </div>
          </div>
          <div
            className="text-[13px] font-semibold text-stone-700"
            data-testid="step-percentage"
          >
            {pct}%
          </div>
        </div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-stone-200">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${pct}%`,
              background: `linear-gradient(90deg, #C89A4E 0%, #B08A45 100%)`,
            }}
            data-testid="progress-bar"
          />
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Individual step bodies                                                     */
/* -------------------------------------------------------------------------- */
function CourtStep({ values, set }) {
  const showDistrict =
    values.COURT_NAME === "Bombay High Court" ||
    values.COURT_NAME === "Bombay High Court Circuit Bench";
  return (
    <div className="space-y-2.5">
      <CourtDropdown
        value={values.COURT_NAME}
        onChange={(v) => {
          set("COURT_NAME", v);
          // Wipe the District value the moment the user switches to
          // a court that doesn't take a District — the backend
          // suppression is belt-and-braces but this also keeps the
          // form + preview in sync.
          if (
            v !== "Bombay High Court" &&
            v !== "Bombay High Court Circuit Bench"
          ) {
            set("DISTRICT", "");
          }
        }}
      />
      <Row cols={2}>
        <TextField label="Court Place" testId="input-COURT_PLACE"
          value={values.COURT_PLACE} onChange={(v) => set("COURT_PLACE", v)} placeholder="e.g. Kolhapur" />
        <TextField label="Police Station" testId="input-POLICE_STATION"
          value={values.POLICE_STATION} onChange={(v) => set("POLICE_STATION", v)} placeholder="e.g. Miraj Loha Marg" />
      </Row>
      {showDistrict ? (
        <TextField
          label="District"
          testId="input-DISTRICT"
          value={values.DISTRICT}
          onChange={(v) => set("DISTRICT", v)}
          placeholder="e.g. Kolhapur"
        />
      ) : null}
      <Row cols={3}>
        <TextField
          label="Case Prefix"
          testId="input-CASE_PREFIX"
          value={values.CASE_PREFIX}
          onChange={(v) => set("CASE_PREFIX", v)}
          placeholder="C.R. No."
          listId="case-prefix-suggestions"
          options={CASE_PREFIX_SUGGESTIONS}
        />
        <TextField label="Case No." testId="input-CRIME_NO"
          value={values.CRIME_NO} onChange={(v) => set("CRIME_NO", v)} placeholder="94" />
        <TextField label="Year" testId="input-CRIME_YEAR"
          value={values.CRIME_YEAR} onChange={(v) => set("CRIME_YEAR", v)} placeholder="2026" />
      </Row>
      <TextField label="Complainant / Respondent" testId="input-COMPLAINANT"
        value={values.COMPLAINANT} onChange={(v) => set("COMPLAINANT", v)}
        placeholder="e.g. State of Maharashtra / ABC Bank Ltd. / Rahul Patil" />
      <SelectField label="Party 1 Label (shown as -- LABEL)"
        testId="input-PARTY1_LABEL"
        value={values.PARTY1_LABEL || "Complainant"}
        onChange={(v) => set("PARTY1_LABEL", v)}
        options={PARTY1_OPTIONS} />
    </div>
  );
}

function DeponentStep({ values, set }) {
  return (
    <div className="space-y-2.5">
      <TextField label="Deponent Name" testId="input-DEPONENT_NAME"
        value={values.DEPONENT_NAME} onChange={(v) => set("DEPONENT_NAME", v)}
        placeholder="Full name of the person swearing this affidavit" />
      <Row cols={2}>
        <TextField label="Age" testId="input-DEPONENT_AGE"
          value={values.DEPONENT_AGE} onChange={(v) => set("DEPONENT_AGE", v)} placeholder="48" />
        <TextField label="Occupation" testId="input-DEPONENT_OCC"
          value={values.DEPONENT_OCC} onChange={(v) => set("DEPONENT_OCC", v)} placeholder="Service" />
      </Row>
      <TextArea label="Address" testId="input-DEPONENT_ADDRESS"
        value={values.DEPONENT_ADDRESS} onChange={(v) => set("DEPONENT_ADDRESS", v)}
        placeholder="Full residential address" rows={2} />
      <Row cols={2}>
        <TextField label="State" testId="input-DEPONENT_STATE"
          value={values.DEPONENT_STATE} onChange={(v) => set("DEPONENT_STATE", v)} placeholder="e.g. Maharashtra" />
        <TextField
          label="Relation with Accused / Information Provided By"
          testId="input-RELATION"
          value={values.RELATION}
          onChange={(v) => set("RELATION", v)}
          placeholder="Select or type (e.g. Husband, Brother, Client)"
          listId="relation-suggestions"
          options={RELATION_OPTIONS}
        />
      </Row>
    </div>
  );
}

function CaseStep({ values, set }) {
  return (
    <div className="space-y-2.5">
      <Row cols={2}>
        <SelectField label="Party 2 Label" testId="input-PARTY2_LABEL"
          value={values.PARTY2_LABEL || "Accused"}
          onChange={(v) => set("PARTY2_LABEL", v)}
          options={PARTY2_OPTIONS} />
        <TextField label="State & District" testId="input-CASE_STATE"
          value={values.CASE_STATE} onChange={(v) => set("CASE_STATE", v)} placeholder="e.g. Maharashtra" />
      </Row>
      <TextField label="Party Name (shown in caption)" testId="input-PARTY2_NAME"
        value={values.PARTY2_NAME}
        onChange={(v) => set("PARTY2_NAME", v.replace(/[0-9]/g, ""))}
        placeholder="e.g. Suraj More" />
      <Row cols={2}>
        <TextField label="FIR Number" testId="input-FIR_NO"
          value={values.FIR_NO} onChange={(v) => set("FIR_NO", v)} placeholder="94/2026" />
        <TextField label="FIR Date" testId="input-FIR_DATE"
          value={values.FIR_DATE} onChange={(v) => set("FIR_DATE", v)} placeholder="DD/MM/YYYY" />
      </Row>
      <SectionsPunishmentsList values={values} set={set} />

      <SectionLabel>Custody &amp; Procedural Status</SectionLabel>
      <Row cols={3}>
        <TextField label="Date of Arrest" testId="input-ARREST_DATE"
          value={values.ARREST_DATE} onChange={(v) => set("ARREST_DATE", v)} placeholder="DD/MM/YYYY" />
        <TextField label="Custody Status" testId="input-CUSTODY_STATUS"
          value={values.CUSTODY_STATUS} onChange={(v) => set("CUSTODY_STATUS", v)}
          placeholder="In Custody / On Bail" />
        <TextField label="Period in Custody" testId="input-CUSTODY_PERIOD"
          value={values.CUSTODY_PERIOD} onChange={(v) => set("CUSTODY_PERIOD", v)} placeholder="18 days" />
      </Row>

      <SectionLabel>Status of Investigation / Trial</SectionLabel>
      <Row cols={3}>
        <TextField label="Stage of Proceeding" testId="input-STAGE"
          value={values.STAGE} onChange={(v) => set("STAGE", v)} placeholder="e.g. In PCR" />
        <TextField label="Witnesses Cited" testId="input-WITNESSES_CITED"
          value={values.WITNESSES_CITED} onChange={(v) => set("WITNESSES_CITED", v)} />
        <TextField label="Witnesses Examined" testId="input-WITNESSES_EXAMINED"
          value={values.WITNESSES_EXAMINED} onChange={(v) => set("WITNESSES_EXAMINED", v)} />
      </Row>

      <SectionLabel>Criminal Antecedents (if any)</SectionLabel>
      <AntecedentsList values={values} set={set} />

      <SectionLabel>Previous Bail Applications (if any)</SectionLabel>
      <PrevBailList values={values} set={set} />

      <SectionLabel>Coercive Process (if any)</SectionLabel>
      <Row cols={3}>
        <TextField label="Non-Bailable Warrant" testId="input-NBW"
          value={values.NBW} onChange={(v) => set("NBW", v)} />
        <TextField label="Proclaimed Offender" testId="input-PROCLAIMED"
          value={values.PROCLAIMED} onChange={(v) => set("PROCLAIMED", v)} />
        <TextField label="Any Other" testId="input-OTHER_COERCIVE"
          value={values.OTHER_COERCIVE} onChange={(v) => set("OTHER_COERCIVE", v)} />
      </Row>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="mt-3 mb-2 text-[14px] font-semibold text-stone-900">
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Dynamic Section + Punishment array (§3 · §4 · §11)                        */
/*  UI-only view over `values.SECTIONS` / `values.MAX_PUNISHMENT`, which      */
/*  remain the single source of truth as newline-joined strings. The backend  */
/*  post-render pass converts each `\n` into a soft <w:br/> so both cells     */
/*  stack their rows in lock-step inside the existing Case Details table —    */
/*  no new tables are ever inserted and the 4-page layout is preserved.       */
/* -------------------------------------------------------------------------- */
function SectionsPunishmentsList({ values, set }) {
  const secs = (values.SECTIONS || "").split("\n");
  const puns = (values.MAX_PUNISHMENT || "").split("\n");
  const len = Math.max(secs.length, puns.length, 1);
  const rows = Array.from({ length: len }, (_, i) => ({
    section: secs[i] || "",
    punishment: puns[i] || "",
  }));

  const writeRows = (list) => {
    // Persist the array as newline-joined strings without any trimming
    // (that runs backend-side). This keeps addRow / removeRow / edit
    // simple — appending an empty row survives the round-trip, so the
    // UI can grow beyond a single row.
    set("SECTIONS", list.map((r) => r.section).join("\n"));
    set("MAX_PUNISHMENT", list.map((r) => r.punishment).join("\n"));
  };

  const updateRow = (idx, field, val) => {
    // Sanitize any accidental newlines from paste
    const clean = String(val || "").replace(/[\r\n]+/g, " ");
    const next = rows.map((r, i) => (i === idx ? { ...r, [field]: clean } : r));
    writeRows(next);
  };
  const addRow = () => {
    if (rows.length >= 8) return; // soft cap keeps document within 4 pages
    writeRows([...rows, { section: "", punishment: "" }]);
  };
  const removeRow = (idx) => {
    if (rows.length <= 1) {
      writeRows([{ section: "", punishment: "" }]);
      return;
    }
    writeRows(rows.filter((_, i) => i !== idx));
  };

  return (
    <div data-testid="sections-punishments-list">
      <div className="mb-1.5 flex items-center justify-between">
        <div className="text-[11.5px] font-medium tracking-wide text-stone-500">
          Sections &amp; Maximum Punishment
        </div>
        <div className="text-[10.5px] text-stone-400">
          {rows.length}/8 rows
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((r, idx) => (
          <div
            key={`sec-row-${idx}`}
            className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]"
            data-testid={`sec-pun-row-${idx}`}
          >
            <input
              type="text"
              value={r.section}
              onChange={(e) => updateRow(idx, "section", e.target.value)}
              placeholder={idx === 0 ? "e.g. BNS 115(2), 352" : "Section"}
              data-testid={`input-SECTION-${idx}`}
              className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] text-stone-900 outline-none transition-colors focus:border-stone-400"
            />
            <input
              type="text"
              value={r.punishment}
              onChange={(e) => updateRow(idx, "punishment", e.target.value)}
              placeholder={idx === 0 ? "e.g. Upto 7 years" : "Max Punishment"}
              data-testid={`input-PUNISHMENT-${idx}`}
              className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] text-stone-900 outline-none transition-colors focus:border-stone-400"
            />
            <button
              type="button"
              onClick={() => removeRow(idx)}
              aria-label="Remove row"
              data-testid={`btn-remove-sec-pun-${idx}`}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-500 hover:border-red-300 hover:text-red-600 sm:justify-self-center"
              disabled={rows.length <= 1 && !r.section && !r.punishment}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addRow}
        disabled={rows.length >= 8}
        data-testid="btn-add-sec-pun-row"
        className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-full border border-dashed border-stone-300 bg-white px-3 text-[12.5px] font-medium text-stone-700 hover:border-stone-400 disabled:cursor-not-allowed disabled:opacity-50"
        style={{ color: GOLD, borderColor: rows.length >= 8 ? undefined : "rgba(176,138,69,0.45)" }}
      >
        <Plus className="h-3.5 w-3.5" />
        Add Section &amp; Punishment
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Dynamic Criminal Antecedents rows                                         */
/*                                                                            */
/*  UI shows a horizontal row per Antecedent with 4 fields in the exact       */
/*  order: FIR No. | Police Station | Sections | Status. A "+" button at the  */
/*  end appends a fresh independent row. The values are stored under          */
/*  `values.ANT_ROWS` as an array of `{fir_no, police_station, sections,      */
/*  status}` objects and shipped to the backend which iterates them into the  */
/*  new horizontal preview table.                                             */
/* -------------------------------------------------------------------------- */
const ANT_EMPTY = { fir_no: "", police_station: "", sections: "", status: "" };
function AntecedentsList({ values, set }) {
  const rows =
    Array.isArray(values.ANT_ROWS) && values.ANT_ROWS.length
      ? values.ANT_ROWS
      : [{ ...ANT_EMPTY }];
  const write = (next) => set("ANT_ROWS", next);
  const update = (idx, field, val) => {
    const clean = String(val || "").replace(/[\r\n]+/g, " ");
    write(rows.map((r, i) => (i === idx ? { ...r, [field]: clean } : r)));
  };
  const addRow = () => {
    if (rows.length >= 6) return;
    write([...rows, { ...ANT_EMPTY }]);
  };
  const removeRow = (idx) => {
    if (rows.length <= 1) return write([{ ...ANT_EMPTY }]);
    write(rows.filter((_, i) => i !== idx));
  };
  return (
    <div data-testid="antecedents-list" className="space-y-2">
      <div className="grid grid-cols-1 gap-1.5 text-[10.5px] font-medium uppercase tracking-wide text-stone-500 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]">
        <div>FIR No.</div>
        <div>Police Station</div>
        <div>Sections</div>
        <div>Status (Pending / Acquitted / Convicted)</div>
        <div />
      </div>
      {rows.map((r, idx) => (
        <div
          key={`ant-row-${idx}`}
          className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]"
          data-testid={`ant-row-${idx}`}
        >
          <input type="text" value={r.fir_no}
            onChange={(e) => update(idx, "fir_no", e.target.value)}
            placeholder="45/2026" data-testid={`input-ANT_FIR_NO-${idx}`}
            className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] outline-none focus:border-stone-400" />
          <input type="text" value={r.police_station}
            onChange={(e) => update(idx, "police_station", e.target.value)}
            placeholder="Shahupuri" data-testid={`input-ANT_POLICE-${idx}`}
            className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] outline-none focus:border-stone-400" />
          <input type="text" value={r.sections}
            onChange={(e) => update(idx, "sections", e.target.value)}
            placeholder="34, 305" data-testid={`input-ANT_SECTIONS-${idx}`}
            className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] outline-none focus:border-stone-400" />
          <input type="text" value={r.status}
            onChange={(e) => update(idx, "status", e.target.value)}
            placeholder="Pending / Acquitted / Convicted"
            data-testid={`input-ANT_STATUS-${idx}`}
            className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] outline-none focus:border-stone-400" />
          <button type="button" onClick={() => removeRow(idx)}
            aria-label="Remove row" data-testid={`btn-remove-ant-${idx}`}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-500 hover:border-red-300 hover:text-red-600 sm:justify-self-center"
            disabled={rows.length <= 1 && !r.fir_no && !r.police_station && !r.sections && !r.status}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={addRow} disabled={rows.length >= 6}
        data-testid="btn-add-ant-row"
        className="mt-1 inline-flex h-9 items-center gap-1.5 rounded-full border border-dashed px-3 text-[12.5px] font-medium hover:border-stone-400 disabled:cursor-not-allowed disabled:opacity-50"
        style={{ color: GOLD, borderColor: "rgba(176,138,69,0.45)" }}>
        <Plus className="h-3.5 w-3.5" />
        Add Criminal Antecedent
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Dynamic Previous Bail Applications rows                                   */
/*                                                                            */
/*  Same pattern as AntecedentsList but with 4 columns in the exact order:    */
/*  Court | Case No. | Date | Result. Values stored under                     */
/*  `values.PREV_BAIL_ROWS` as an array of                                    */
/*  `{court, case_no, date, result}` objects.                                 */
/* -------------------------------------------------------------------------- */
const PB_EMPTY = { court: "", case_no: "", date: "", result: "" };
function PrevBailList({ values, set }) {
  const rows =
    Array.isArray(values.PREV_BAIL_ROWS) && values.PREV_BAIL_ROWS.length
      ? values.PREV_BAIL_ROWS
      : [{ ...PB_EMPTY }];
  const write = (next) => set("PREV_BAIL_ROWS", next);
  const update = (idx, field, val) => {
    const clean = String(val || "").replace(/[\r\n]+/g, " ");
    write(rows.map((r, i) => (i === idx ? { ...r, [field]: clean } : r)));
  };
  const addRow = () => {
    if (rows.length >= 6) return;
    write([...rows, { ...PB_EMPTY }]);
  };
  const removeRow = (idx) => {
    if (rows.length <= 1) return write([{ ...PB_EMPTY }]);
    write(rows.filter((_, i) => i !== idx));
  };
  return (
    <div data-testid="prev-bail-list" className="space-y-2">
      <div className="grid grid-cols-1 gap-1.5 text-[10.5px] font-medium uppercase tracking-wide text-stone-500 sm:grid-cols-[1.4fr_1fr_1fr_1fr_auto]">
        <div>Court</div>
        <div>Case No.</div>
        <div>Date</div>
        <div>Result</div>
        <div />
      </div>
      {rows.map((r, idx) => (
        <div
          key={`pb-row-${idx}`}
          className="grid grid-cols-1 gap-2 sm:grid-cols-[1.4fr_1fr_1fr_1fr_auto]"
          data-testid={`pb-row-${idx}`}
        >
          <input type="text" value={r.court}
            onChange={(e) => update(idx, "court", e.target.value)}
            placeholder="Bombay High Court"
            data-testid={`input-PREV_BAIL_COURT-${idx}`}
            className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] outline-none focus:border-stone-400" />
          <input type="text" value={r.case_no}
            onChange={(e) => update(idx, "case_no", e.target.value)}
            placeholder="123/2026"
            data-testid={`input-PREV_BAIL_CASE-${idx}`}
            className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] outline-none focus:border-stone-400" />
          <input type="text" value={r.date}
            onChange={(e) => update(idx, "date", e.target.value)}
            placeholder="DD/MM/YYYY"
            data-testid={`input-PREV_BAIL_DATE-${idx}`}
            className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] outline-none focus:border-stone-400" />
          <input type="text" value={r.result}
            onChange={(e) => update(idx, "result", e.target.value)}
            placeholder="Pending / Rejected / Granted"
            data-testid={`input-PREV_BAIL_RESULT-${idx}`}
            className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] outline-none focus:border-stone-400" />
          <button type="button" onClick={() => removeRow(idx)}
            aria-label="Remove row" data-testid={`btn-remove-pb-${idx}`}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-500 hover:border-red-300 hover:text-red-600 sm:justify-self-center"
            disabled={rows.length <= 1 && !r.court && !r.case_no && !r.date && !r.result}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={addRow} disabled={rows.length >= 6}
        data-testid="btn-add-pb-row"
        className="mt-1 inline-flex h-9 items-center gap-1.5 rounded-full border border-dashed px-3 text-[12.5px] font-medium hover:border-stone-400 disabled:cursor-not-allowed disabled:opacity-50"
        style={{ color: GOLD, borderColor: "rgba(176,138,69,0.45)" }}>
        <Plus className="h-3.5 w-3.5" />
        Add Previous Bail Application
      </button>
    </div>
  );
}


function VerifyStep({ values, set }) {
  return (
    <div className="space-y-2.5">
      <div>
        <div className="mb-1 text-[11.5px] font-medium tracking-wide text-stone-500">
          Verification Date
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          <input type="text" value={values.VERIFICATION_DAY || ""}
            onChange={(e) => set("VERIFICATION_DAY", e.target.value)} placeholder="Day"
            data-testid="input-VERIFICATION_DAY"
            className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] text-stone-900 outline-none focus:border-stone-400" />
          <input type="text" value={values.VERIFICATION_MONTH || ""}
            onChange={(e) => set("VERIFICATION_MONTH", e.target.value)} placeholder="Month"
            data-testid="input-VERIFICATION_MONTH"
            className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] text-stone-900 outline-none focus:border-stone-400" />
          <input type="text" value={values.VERIFICATION_YEAR || ""}
            onChange={(e) => set("VERIFICATION_YEAR", e.target.value)} placeholder="Year"
            data-testid="input-VERIFICATION_YEAR"
            className="h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-[13.5px] text-stone-900 outline-none focus:border-stone-400" />
        </div>
        <div className="mt-1.5 text-[11px] text-stone-500">
          Verification place is automatically taken from the Court Place value.
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Inline-editable field groups shown alongside the Preview so the user      */
/*  never has to return to the form to correct a value. Court template text,  */
/*  headings, tables and legal drafting are NEVER editable — only the         */
/*  dynamic fields listed here.                                               */
/* -------------------------------------------------------------------------- */
const EDITABLE_GROUPS = [
  {
    title: "Court",
    fields: [
      { key: "COURT_NAME", label: "Court Name" },
      { key: "COURT_PLACE", label: "Court Place" },
      { key: "POLICE_STATION", label: "Police Station" },
      { key: "CASE_PREFIX", label: "Case Prefix" },
      { key: "CRIME_NO", label: "Crime No." },
      { key: "CRIME_YEAR", label: "Crime Year" },
    ],
  },
  {
    title: "Parties",
    fields: [
      { key: "COMPLAINANT", label: "Complainant / Party 1 Name" },
      { key: "PARTY1_LABEL", label: "Party 1 Label" },
      { key: "PARTY2_NAME", label: "Accused / Party 2 Name" },
      { key: "PARTY2_LABEL", label: "Party 2 Label" },
    ],
  },
  {
    title: "Deponent",
    fields: [
      { key: "DEPONENT_NAME", label: "Deponent Name" },
      { key: "DEPONENT_AGE", label: "Age" },
      { key: "DEPONENT_OCC", label: "Occupation" },
      { key: "DEPONENT_ADDRESS", label: "Address" },
      { key: "DEPONENT_STATE", label: "State" },
      { key: "RELATION", label: "Relation with Accused / Information Provided By" },
    ],
  },
  {
    title: "Case Details",
    fields: [
      { key: "CASE_STATE", label: "Case State & District" },
      { key: "FIR_NO", label: "FIR No." },
      { key: "FIR_DATE", label: "Date of FIR" },
      { key: "SECTIONS", label: "Sections Invoked" },
      { key: "MAX_PUNISHMENT", label: "Max Punishment" },
    ],
  },
  {
    title: "Custody & Status",
    fields: [
      { key: "ARREST_DATE", label: "Arrest Date" },
      { key: "CUSTODY_STATUS", label: "Custody Status" },
      { key: "CUSTODY_PERIOD", label: "Custody Period" },
      { key: "STAGE", label: "Stage of Investigation" },
      { key: "WITNESSES_CITED", label: "Witnesses Cited" },
      { key: "WITNESSES_EXAMINED", label: "Witnesses Examined" },
    ],
  },
  {
    title: "Coercive Process",
    fields: [
      { key: "NBW", label: "Non-Bailable Warrant" },
      { key: "PROCLAIMED", label: "Proclaimed Offender" },
      { key: "OTHER_COERCIVE", label: "Other Coercive Process" },
    ],
  },
  {
    title: "Verification",
    fields: [
      { key: "VERIFICATION_PLACE", label: "Verification Place" },
      { key: "VERIFICATION_DAY", label: "Day" },
      { key: "VERIFICATION_MONTH", label: "Month" },
      { key: "VERIFICATION_YEAR", label: "Year" },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/*  Preview screen — Image-based, view-only, with in-place click-to-edit      */
/*  overlays on every dynamic field. Court template, headings, tables, and    */
/*  legal drafting stay locked; only user-entered values respond to clicks.   */
/* -------------------------------------------------------------------------- */
function PreviewScreen({
  values,
  set,
  onEdit,
  onSave,
  onDownload,
  saving,
  downloading,
}) {
  const [pages, setPages] = useState([]);            // array of PNG data URLs
  const [bboxes, setBboxes] = useState([]);          // [{key,page,box:[x,y,w,h],value}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [fit, setFit] = useState(true);
  const [activeKey, setActiveKey] = useState(null);  // field currently being edited
  const [editMode, setEditMode] = useState(false);   // toggle for editable-highlight overlay
  const [debouncedValues, setDebouncedValues] = useState(values);

  useEffect(() => {
    // Live preview (§6): debounce every keystroke — form OR inline
    // preview edit — so the server-rendered pages refresh ~400 ms
    // after the user pauses typing. Enter / blur is NOT required.
    // The debounce is short enough to feel instant while high enough
    // that a fast typist doesn't queue five renders in a row.
    const t = setTimeout(() => setDebouncedValues(values), 400);
    return () => clearTimeout(t);
  }, [values]);

  useEffect(() => {
    let cancelled = false;
    const abort = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/bail-affidavit/render-preview-images`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ fields: debouncedValues }),
            signal: abort.signal,
          }
        );
        if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setPages(Array.isArray(data.pages) ? data.pages : []);
        setBboxes(Array.isArray(data.field_bboxes) ? data.field_bboxes : []);
      } catch (e) {
        if (e.name !== "AbortError") setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [debouncedValues]);

  // --- Block Ctrl+S / Ctrl+P / Cmd+S / Cmd+P while previewing ---
  useEffect(() => {
    const blockKey = (e) => {
      const meta = e.ctrlKey || e.metaKey;
      const k = (e.key || "").toLowerCase();
      if (meta && (k === "s" || k === "p")) {
        e.preventDefault();
        e.stopPropagation();
        toast.info(
          "Downloads are locked until payment. Preview is view-only."
        );
      }
    };
    window.addEventListener("keydown", blockKey, { capture: true });
    return () =>
      window.removeEventListener("keydown", blockKey, { capture: true });
  }, []);

  const zoomIn = () => {
    setFit(false);
    setZoom((z) => Math.min(2.5, +(z + 0.1).toFixed(2)));
  };
  const zoomOut = () => {
    setFit(false);
    setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)));
  };
  const fitScreen = () => {
    setFit(true);
    setZoom(1);
  };
  const pct = fit ? "Fit" : `${Math.round(zoom * 100)}%`;

  return (
    <div
      className="min-h-screen w-full pb-28"
      style={{ background: "#FAF8F4", fontFamily: INTER }}
      data-testid="bail-preview-page"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-20 w-full border-b border-stone-200/80 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-3xl items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onEdit}
              data-testid="btn-preview-back"
              className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-stone-100"
              aria-label="Back to form"
            >
              <ArrowLeft className="h-5 w-5 text-stone-700" />
            </button>
            <div className="text-[14.5px] font-semibold text-stone-900">
              Bail Affidavit · Preview
            </div>
          </div>
          <div className="flex items-center gap-2">
            {editMode ? (
              <button
                type="button"
                onClick={() => {
                  setActiveKey(null);
                  setEditMode(false);
                }}
                data-testid="btn-preview-done"
                className="flex h-9 items-center gap-1.5 rounded-full px-4 text-[12.5px] font-semibold text-white shadow"
                style={{ background: "linear-gradient(135deg,#C89A4E,#8a6a2f)" }}
              >
                Done
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setEditMode(true)}
                data-testid="btn-preview-edit"
                className="flex h-9 items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 text-[12.5px] font-medium text-stone-700 hover:bg-stone-50"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Info strip with zoom controls */}
      <div className="mx-auto max-w-3xl px-4 pt-4">
        <div
          className="flex items-center gap-3 rounded-2xl bg-white p-3"
          style={{
            boxShadow:
              "0 10px 30px -22px rgba(15,23,42,0.20), inset 0 0 0 1px rgba(15,23,42,0.05)",
          }}
        >
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
            style={{ background: "rgba(176,138,69,0.14)" }}
          >
            <Eye className="h-4 w-4" style={{ color: GOLD }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-stone-900">
              Court-Ready PDF Preview
            </div>
            <div className="text-[11.5px] text-stone-500">
              {editMode
                ? "Edit mode ON · Highlighted values are editable · click any value to edit, then Done to finish."
                : "Click any user-entered value to edit it in place · Downloads unlock after payment."}
            </div>
          </div>
          <div
            className="flex items-center gap-1 rounded-full border border-stone-200 bg-stone-50 p-0.5"
            data-testid="zoom-controls"
          >
            <button
              type="button"
              onClick={zoomOut}
              data-testid="btn-zoom-out"
              className="flex h-8 w-8 items-center justify-center rounded-full text-stone-600 hover:bg-white"
              aria-label="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={fitScreen}
              data-testid="btn-zoom-fit"
              className="flex h-8 min-w-[54px] items-center justify-center gap-1 rounded-full px-2 text-[11.5px] font-medium hover:bg-white"
              style={{
                color: fit ? "#1c1917" : "#6b7280",
                background: fit ? "#fff" : "transparent",
                boxShadow: fit ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
              }}
              aria-label="Fit to screen"
              title="Fit to screen"
            >
              <Maximize2 className="h-3.5 w-3.5" />
              {pct}
            </button>
            <button
              type="button"
              onClick={zoomIn}
              data-testid="btn-zoom-in"
              className="flex h-8 w-8 items-center justify-center rounded-full text-stone-600 hover:bg-white"
              aria-label="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Image-based preview (view-only, watermarked, mobile-friendly) */}
      <div
        className="mx-auto mt-3 max-w-3xl px-4"
        onContextMenu={(e) => e.preventDefault()}
        data-testid="bail-preview-wrapper"
      >
        {loading && pages.length === 0 && (
          <div className="flex h-[70vh] items-center justify-center rounded-2xl bg-white/60">
            <div className="flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-[12.5px] text-stone-600 shadow">
              <Loader2 className="h-4 w-4 animate-spin" /> Rendering preview…
            </div>
          </div>
        )}
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-[12.5px] text-red-700">
            Preview failed: {error}
          </div>
        )}
        <div className="flex flex-col items-center gap-4">
          {pages.map((src, i) => (
            <div
              key={`page-${i}`}
              className="relative w-full overflow-hidden rounded-2xl bg-white"
              style={{
                boxShadow:
                  "0 20px 45px -25px rgba(15,23,42,0.20), inset 0 0 0 1px rgba(15,23,42,0.05)",
                width: fit ? "100%" : `${Math.round(zoom * 100)}%`,
                maxWidth: "100%",
              }}
            >
              <img
                src={src}
                alt={`Bail Affidavit — Page ${i + 1}`}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
                className="block w-full select-none"
                style={{
                  pointerEvents: "auto",
                  WebkitUserSelect: "none",
                  userSelect: "none",
                  WebkitTouchCallout: "none",
                }}
                data-testid={`bail-preview-image-${i + 1}`}
              />
              {/* Click-in-document editable overlays. Each entry is a
                  transparent rectangle positioned over the exact spot
                  in the rendered PDF where a user-entered value lives.
                  Clicking one turns it into an inline input; every
                  other pixel of the document remains locked. */}
              {bboxes
                .filter((b) => b.page === i)
                .map((b) => {
                  const [x, y, w, h] = b.box;
                  const isActive = activeKey === b.key;
                  return (
                    <div
                      key={`${b.key}-${i}`}
                      className="absolute"
                      style={{
                        left: `${x * 100}%`,
                        top: `${y * 100}%`,
                        width: `${Math.max(w, 0.08) * 100}%`,
                        height: `${Math.max(h, 0.014) * 100}%`,
                        zIndex: isActive ? 40 : 20,
                      }}
                    >
                      {isActive ? (
                        (b.key === "SECTIONS" ||
                        b.key === "MAX_PUNISHMENT" ||
                        (values[b.key] || "").includes("\n")) ? (
                        <textarea
                          autoFocus
                          value={values[b.key] || ""}
                          onChange={(e) => set(b.key, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              e.preventDefault();
                              setActiveKey(null);
                              e.currentTarget.blur();
                            }
                          }}
                          onBlur={() => setActiveKey(null)}
                          data-testid={`edit-${b.key}`}
                          rows={Math.max(2, (values[b.key] || "").split("\n").length)}
                          className="absolute left-0 top-0 h-auto min-h-full w-full resize-y rounded-[3px] border border-amber-500 bg-white px-1 text-[13px] leading-[1.15] text-stone-900 shadow-sm outline-none ring-2 ring-amber-300/40"
                          style={{
                            maxWidth: `calc(100vw - ${Math.round(x * 100)}vw - 24px)`,
                          }}
                        />
                        ) : (
                        <input
                          autoFocus
                          type="text"
                          value={values[b.key] || ""}
                          onChange={(e) => set(b.key, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === "Escape") {
                              e.preventDefault();
                              setActiveKey(null);
                              e.currentTarget.blur();
                            }
                          }}
                          onBlur={() => setActiveKey(null)}
                          data-testid={`edit-${b.key}`}
                          className="absolute left-0 top-0 h-auto min-h-full rounded-[3px] border border-amber-500 bg-white px-1 text-[13px] leading-[1.1] text-stone-900 shadow-sm outline-none ring-2 ring-amber-300/40"
                          style={{
                            fieldSizing: "content",
                            minWidth: "100%",
                            maxWidth: `calc(100vw - ${Math.round(x * 100)}vw - 24px)`,
                            width: "auto",
                            whiteSpace: "nowrap",
                          }}
                        />
                        )
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setActiveKey(b.key)}
                            aria-label={`Edit ${b.key.toLowerCase().replace(/_/g, " ")}`}
                            data-testid={`hitbox-${b.key}`}
                            className={`h-full w-full cursor-text rounded-[2px] transition-colors ${
                              editMode
                                ? "bg-amber-200/35 outline outline-1 outline-amber-400/60 hover:bg-amber-300/45"
                                : "hover:bg-amber-200/25"
                            }`}
                            style={{ background: editMode ? undefined : "transparent" }}
                          />
                          {/* Optimistic overlay: while the server is still
                              re-rendering after a commit, mask the old
                              value in the underlying image with the
                              latest state value so the preview never
                              shows stale text. */}
                          {loading && (values[b.key] || "") !== (b.value || "") && (
                            <div
                              className="pointer-events-none absolute inset-0 flex items-center overflow-hidden bg-white px-1 text-[13px] leading-[1.1] text-stone-900"
                              style={{ whiteSpace: "nowrap" }}
                              data-testid={`optimistic-${b.key}`}
                            >
                              {values[b.key] || ""}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              {/* Belt-and-suspenders: transparent overlay to defeat some
                  long-press "Save Image" contexts on mobile browsers.
                  Kept below hit-boxes so clicks still reach them. */}
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{ pointerEvents: "none", background: "transparent", zIndex: 10 }}
              />
              <div className="pointer-events-none absolute bottom-2 right-3 rounded-full bg-black/40 px-2 py-0.5 text-[10px] font-medium text-white" style={{ zIndex: 15 }}>
                Page {i + 1} of {pages.length}
              </div>
            </div>
          ))}
        </div>
        {loading && pages.length > 0 && (
          <div className="mt-3 flex items-center justify-center text-[11.5px] text-stone-500">
            <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> Updating preview…
          </div>
        )}
      </div>

      {/* Sticky bottom actions */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-stone-200 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            data-testid="btn-save-bail-case"
            className="flex h-12 flex-1 items-center justify-center gap-1.5 rounded-2xl border border-stone-200 bg-white text-[14px] font-semibold text-stone-800 transition-all hover:-translate-y-[1px] disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save Case
          </button>
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            data-testid="btn-download-bail-pdf"
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl text-[14.5px] font-semibold text-white transition-all hover:-translate-y-[1px] disabled:opacity-60"
            style={{
              background: "linear-gradient(135deg, #C89A4E 0%, #B08A45 55%, #8a6a2f 100%)",
              boxShadow: "0 12px 24px -12px rgba(176,138,69,0.55)",
            }}
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download PDF · ₹40
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */
export default function BailAffidavitPage() {
  const { route, navigate } = useNav();
  const { user } = useAuth();
  const match = matchRoute(route, "/criminal/bail-affidavit/:caseId");
  const caseId = match?.params?.caseId || null;

  const [values, setValues] = useState(DEFAULT_VALUES);
  const [stepIdx, setStepIdx] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [savedId, setSavedId] = useState(caseId);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  const set = useCallback((k, v) => {
    setValues((prev) => ({ ...prev, [k]: v }));
  }, []);

  // Load saved case (if editing)
  useEffect(() => {
    if (!caseId) return;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/bail-cases/${caseId}`, {
          credentials: "include",
        });
        if (res.ok) {
          const c = await res.json();
          setValues({ ...DEFAULT_VALUES, ...(c.fields || {}) });
          setSavedId(c.id);
        } else if (res.status === 404) {
          toast.error("Case not found");
          navigate("/criminal");
        }
      } catch (_) {
        toast.error("Failed to load case");
      }
    })();
  }, [caseId, navigate]);

  const saveCase = async () => {
    setSaving(true);
    try {
      const body = JSON.stringify({ title: "", fields: values });
      const res = savedId
        ? await fetch(`${BACKEND_URL}/api/bail-cases/${savedId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body,
          })
        : await fetch(`${BACKEND_URL}/api/bail-cases`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body,
          });
      if (!res.ok) throw new Error(String(res.status));
      const c = await res.json();
      setSavedId(c.id);
      toast.success("Case saved");
    } catch (_) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const generatePdfAfterPayment = async (downloadToken) => {
    setDownloading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/bail-affidavit/download-pdf`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ fields: values, download_token: downloadToken }),
        }
      );
      if (!res.ok) throw new Error(`PDF failed: ${res.status}`);
      const blob = await res.blob();
      // Auto-trigger browser download of the paid PDF, then release the
      // blob URL. No intermediate viewer — reduces the window during
      // which the paid PDF is reachable from anywhere except the user's
      // download folder.
      const url = URL.createObjectURL(blob);
      const filename = `Bail_Affidavit_${(values.PARTY2_NAME || values.DEPONENT_NAME || "case")
        .replace(/\s+/g, "_")}.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Give the browser a tick to start the download, then revoke.
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Download started");
    } catch (_) {
      toast.error("PDF generation failed");
    } finally {
      setDownloading(false);
    }
  };

  const onNext = () => {
    if (stepIdx < STEPS.length - 1) {
      setStepIdx(stepIdx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // On last step "Next" opens the Preview
      setShowPreview(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const onBack = () => {
    if (stepIdx > 0) {
      setStepIdx(stepIdx - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const currentStep = STEPS[stepIdx];
  const isFinalStep = stepIdx === STEPS.length - 1;

  // ---------------------------------------------------------------
  // 1) If user has finished the form, show Preview screen
  // ---------------------------------------------------------------
  if (showPreview) {
    return (
      <>
        <PreviewScreen
          values={values}
          set={set}
          onEdit={() => setShowPreview(false)}
          onSave={saveCase}
          onDownload={() => setPayOpen(true)}
          saving={saving}
          downloading={downloading}
        />
        <BailPaymentDialog
          open={payOpen}
          onOpenChange={setPayOpen}
          userName={user?.name || ""}
          userEmail={user?.email || ""}
          onPaid={async (downloadToken) => {
            setPayOpen(false);
            await generatePdfAfterPayment(downloadToken);
          }}
        />
      </>
    );
  }

  return (
    <div
      className="min-h-screen w-full pb-28"
      style={{ background: "#FAF8F4", fontFamily: INTER }}
      data-testid="bail-affidavit-page"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-20 w-full border-b border-stone-200/80 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/criminal")}
              data-testid="btn-back-criminal"
              className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-stone-100"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5 text-stone-700" />
            </button>
            <div className="text-[14.5px] font-semibold text-stone-900">
              Bail Affidavit
            </div>
          </div>
        </div>
      </header>

      <StepHeader stepIdx={stepIdx} />

      {/* Body */}
      <div className="mx-auto max-w-4xl px-4 pt-4">
        {currentStep.id === "court" && <CourtStep values={values} set={set} />}
        {currentStep.id === "deponent" && <DeponentStep values={values} set={set} />}
        {currentStep.id === "case" && <CaseStep values={values} set={set} />}
        {currentStep.id === "verify" && <VerifyStep values={values} set={set} />}
      </div>

      {/* Sticky bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-stone-200 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={onBack}
            disabled={stepIdx === 0}
            data-testid="btn-step-back"
            className="flex h-12 w-24 items-center justify-center gap-1.5 rounded-2xl border border-stone-200 bg-white text-[14px] font-medium text-stone-700 transition-all hover:-translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          {isFinalStep ? (
            <button
              type="button"
              onClick={onNext}
              data-testid="btn-open-preview"
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl text-[14.5px] font-semibold text-white transition-all hover:-translate-y-[1px]"
              style={{
                background: "linear-gradient(135deg, #C89A4E 0%, #B08A45 55%, #8a6a2f 100%)",
                boxShadow: "0 12px 24px -12px rgba(176,138,69,0.55)",
              }}
            >
              <Eye className="h-4 w-4" />
              Preview
            </button>
          ) : (
            <button
              type="button"
              onClick={onNext}
              data-testid="btn-step-next"
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl text-[14.5px] font-semibold text-white transition-all hover:-translate-y-[1px]"
              style={{
                background: "linear-gradient(135deg, #C89A4E 0%, #B08A45 55%, #8a6a2f 100%)",
                boxShadow: "0 12px 24px -12px rgba(176,138,69,0.55)",
              }}
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <BailPaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        userName={user?.name || ""}
        userEmail={user?.email || ""}
        onPaid={async (downloadToken) => {
          setPayOpen(false);
          await generatePdfAfterPayment(downloadToken);
        }}
      />
    </div>
  );
}
